<template>
    <div class="morphokinetics-table">
        <pn-duc-deg-fix-panel :well="well" />

        <div v-if="annotationsWarning" class="annotations-message">
            <span>{{ annotationsWarning }}</span>
            <img src="/static/svg/Hazard.svg" alt="" class="annotation-hazard-icon" />
        </div>

        <div v-if="hasAnnotations && morphokinetics.length" class="morphokinetics-table-header">
            <div class="event-label-header">
                <span class="gap"></span>
                <div class="title">{{ $translate('event') }}</div>
            </div>

            <div class="value">{{ $translate('at_hour') }}</div>
            <div class="range">{{ $translate('delta_hours') }}</div>
        </div>

        <div class="metrics-container">
            <div v-if="hasAnnotations" class="range-metrics">
                <mk-event-row
                    v-for="event in morphokinetics"
                    :key="event.systemName"
                    class="actual"
                    :class="{ 'current-event': currentEvent === event }"
                    :event="event"
                    :is-duc="duc"
                    @metricClicked="goTo"
                />
            </div>

            <div v-if="hasBigHazardImg" class="big-hazard-img">
                <img
                    v-if="degeneratedOocyte"
                    src="/static/svg/Big_Hazard_Deg.svg"
                    alt=""
                    class="fa"
                />
                <img v-else src="/static/svg/Big_Hazard_Pn0.svg" alt="" class="fa" />
            </div>

            <div v-if="hasAnnotations && predictedEvents.length" class="predicted-range-metrics">
                <div class="predicted-metrics-header" @click.stop.prevent="toggleMkPredictions">
                    <div class="mk-prediction-title px-3 pb-2 pt-1 my-1">
                        <img src="/static/svg/chloe_icon.svg" alt="" />
                        <span class="ml-2">{{ $translate('prediction') }}</span>
                    </div>

                    <span class="px-3 py-2">
                        <b-button variant="transparent">
                            <i
                                class="fa"
                                :class="{
                                    'fa-angle-down': !showPredictions,
                                    'fa-angle-up': showPredictions
                                }"
                            />
                        </b-button>
                    </span>
                </div>

                <div class="predicted-metrics-content py-2" :class="{ hidden: !showPredictions }">
                    <mk-event-row
                        v-for="event in predictedEvents"
                        :key="event.systemName"
                        :event="event"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex'
import MkEventRow from './partials/MkEventRow'
import PnDucDegFixPanel from './partials/PnDucDegFixPanel'

export default {
    name: 'MorphokineticsTable',
    components: { MkEventRow, PnDucDegFixPanel },
    props: {
        well: {
            type: Object,
            required: true
        },
        currentEvent: {
            type: Object,
            default: null
        }
    },
    data: () => ({
        showPredictions: true
    }),
    computed: {
        ...mapGetters({
            checkDucsHazard: 'timelapse/checkDucsHazard',
            checkDegeneratedOocyteHazard: 'timelapse/checkDegeneratedOocyteHazard',
            getPnCount: 'timelapse/getPnCount'
        }),
        morphokinetics() {
            return this.well.inferenceData.morphokinetics
        },
        inferenceOverridingData() {
            return this.well.inferenceOverridingData
        },
        runs() {
            return this.well.runs
        },
        predictedEvents() {
            const { predictMorphokinetics } = this.well.inferenceData

            return predictMorphokinetics.filter(
                item =>
                    item.value &&
                    !this.morphokinetics.some(
                        _item => item.systemName.toLowerCase() === _item.systemName.toLowerCase()
                    )
            )
        },
        fragmented() {
            return this.well.inferenceData.morphokinetics.find(mkEvent => mkEvent.fragmented)
        },
        duc() {
            return this.checkDucsHazard(this.well)
        },
        degeneratedOocyte() {
            return this.checkDegeneratedOocyteHazard(this.well)
        },
        hasAnnotations() {
            return !this.degeneratedOocyte && !this.isNoPn
        },
        noAnnotationsWarning() {
            const hazardKey = this.degeneratedOocyte ? 'degenerated_oocyte' : 'unfertilized_oocyte'
            return `${this.$translate('no_annotations_due_to')} ${this.$translate(hazardKey)}`
        },
        hasBigHazardImg() {
            return this.degeneratedOocyte || this.isNoPn
        },
        isLowConfidence() {
            return this.fragmented || this.duc || this.ducRemovedByAdminOrUser
        },
        ducRemovedByAdminOrUser() {
            // flag "low confidence" is coming true if admin OR user changed duc,
            // ...meaning there were several opinions on the matter
            return !this.duc && this.well.isLowConfidence
        },
        lowConfidenceAnnotationsWarning() {
            if (this.fragmented) {
                return this.$translate('low_confidence_annotations_due_to_fragmentation')
            }
            if (this.duc) {
                return this.$translate('low_confidence_annotations_due_to_duc1')
            }
            if (this.ducRemovedByAdminOrUser) {
                return this.$translate('low_confidence_annotations')
            }

            return ''
        },
        isNoPn() {
            return this.getPnCount(this.well) === 0
        },
        annotationsWarning() {
            if (!this.hasAnnotations) {
                return this.noAnnotationsWarning
            } else if (this.isLowConfidence) {
                return this.lowConfidenceAnnotationsWarning
            }
            return null
        }
    },
    methods: {
        goTo(systemName) {
            const selectedEvent = this.morphokinetics.find(
                rangeMetric => rangeMetric.systemName === systemName
            )
            if (selectedEvent) {
                this.$emit('eventChosen', selectedEvent)
            }
        },
        toggleMkPredictions() {
            this.showPredictions = !this.showPredictions
        }
    }
}
</script>

<style lang="scss" scoped>
.morphokinetics-table {
    .pn-duc-deg-fix-panel {
        @media screen and #{$new-break3} {
            display: none;
        }
    }
}
</style>

<style lang="scss">
.morphokinetics-table {
    position: relative;
    width: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;

    .annotations-message {
        display: flex;
        border: 0.1rem solid $red;
        color: $red;
        padding: 0.5rem 1rem;
        margin: 0.5rem 1rem;
        background-color: $red-alpha;
    }

    .annotation-hazard-icon {
        height: 1.5rem;
        margin-left: auto;
    }

    .morphokinetics-table-header {
        padding: 0.5rem 1rem;
        display: grid;
        column-gap: 0.5rem;
        color: $light;

        .event-label-header {
            display: grid;
            grid-template-columns: 1fr 4fr;
        }

        @media screen and #{$new-break4close} {
            grid-template-columns: 3fr 3fr 2fr;
        }
        @media screen and #{$new-break5open} {
            grid-template-columns: 2fr 3fr 5fr;
        }

        .value,
        .range {
            display: flex;
            align-items: center;
            justify-content: center;
        }
    }

    .metrics-container {
        position: relative;
        display: flex;
        flex-direction: column;
        width: 100%;
        height: 100%;
        overflow-y: auto;

        .range-metrics {
            width: 100%;
            background-color: transparent;
        }

        .big-hazard-img {
            display: flex;
            align-items: center;
            justify-content: center;
            padding-bottom: 3rem;
            flex-grow: 1;

            img {
                margin-bottom: 1.5rem;
                font-size: 5em;
                height: 6rem;
                opacity: 0.7;
            }
        }

        .predicted-range-metrics {
            background-color: $dark-2;
            transition: 0.3s;
            transform-origin: top;
            opacity: 1;

            .predicted-metrics-header {
                display: flex;
                justify-content: space-between;

                .mk-prediction-title {
                    display: flex;
                    align-items: center;
                    font-size: 11pt;

                    img {
                        height: 1rem;
                        margin-bottom: 0;
                    }
                    span {
                        color: $blue;
                        font-weight: var(--font-weight-bold);
                    }
                }

                .fa-angle-down,
                .fa-angle-up {
                    font-size: 26px;
                }
            }

            .predicted-metrics-content {
                &.hidden {
                    display: none;
                }

                .border-bottom-dark-2 {
                    border-color: $dark !important;
                }
            }
        }

        .mk-event-row {
            &:not(:last-child) {
                border-bottom: $border-narrow-dark-2;
            }
            &.actual {
                cursor: pointer;
            }
            &.current-event {
                background-color: $dark-5 !important;
            }

            @media screen and #{$new-break4open} {
                &:hover {
                    background-color: $dark-4;
                }
            }
        }
    }
}
</style>
